import { useCallback, useEffect, useMemo, useState } from "react";
import { getVideos, VideoMeta } from "../../../services/video.service";

type Params = {
  baseTags: string[];
  selectedTags: string[];
  query: string;
  allowedIds: string[];
  pageSize: number;
  savedOnly: boolean;
};

type Result = {
  videos: VideoMeta[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
};

type NormalCacheState = {
  videos: VideoMeta[];
  nextCursor: string | null;
  cacheKey: string;
};

type SavedCacheState = {
  videos: VideoMeta[];
  cacheKey: string;
};

const SAVED_VIDEOS_KEY = "savedVideos";

const normalize = (value: unknown): string =>
  String(value ?? "").trim().toLowerCase();

const readSavedVideos = (): VideoMeta[] => {
  const raw = localStorage.getItem(SAVED_VIDEOS_KEY);

  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const useVideoList = ({
  baseTags,
  selectedTags,
  query,
  allowedIds,
  pageSize,
  savedOnly,
}: Params): Result => {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [normalCache, setNormalCache] = useState<NormalCacheState>({
    videos: [],
    nextCursor: null,
    cacheKey: "",
  });

  const [savedCache, setSavedCache] = useState<SavedCacheState>({
    videos: [],
    cacheKey: "",
  });

  const tagsForApi = useMemo(
    () =>
      [...baseTags, ...selectedTags]
        .map((tag) => tag.trim())
        .filter(Boolean),
    [baseTags, selectedTags]
  );

  const searchForApi = useMemo(() => normalize(query), [query]);

  const filterAllowedAndDeduplicate = useCallback(
    (items: VideoMeta[]): VideoMeta[] => {
      const seen = new Set<string>();

      return items.filter((video) => {
        if (allowedIds.length > 0 && !allowedIds.includes(video.uid)) {
          return false;
        }

        if (seen.has(video.uid)) {
          return false;
        }

        seen.add(video.uid);
        return true;
      });
    },
    [allowedIds]
  );

  const filterSavedVideosLocally = useCallback(
    (items: VideoMeta[]): VideoMeta[] => {
      const deduped = filterAllowedAndDeduplicate(items);

      if (!searchForApi) {
        return deduped;
      }

      return deduped.filter((video) => {
        const title = normalize(video.meta?.title);
        const description = normalize(video.meta?.description);
        const tags = String(video.meta?.tags ?? "")
          .split(",")
          .map(normalize)
          .filter(Boolean)
          .join(" ");
        const category = normalize(video.meta?.category);
        const uid = normalize(video.uid);

        const searchableText = [title, description, tags, category, uid]
          .filter(Boolean)
          .join(" ");

        return searchableText.includes(searchForApi);
      });
    },
    [filterAllowedAndDeduplicate, searchForApi]
  );

  const normalCacheKey = useMemo(
    () =>
      JSON.stringify({
        tags: tagsForApi,
        search: searchForApi,
        allowedIds,
        pageSize,
      }),
    [tagsForApi, searchForApi, allowedIds, pageSize]
  );

  const savedCacheKey = useMemo(
    () =>
      JSON.stringify({
        search: searchForApi,
        allowedIds,
      }),
    [searchForApi, allowedIds]
  );

  const fetchNormalFirstPage = useCallback(async () => {
    setLoading(true);

    try {
      const { items, nextCursor } = await getVideos({
        tags: tagsForApi.length ? tagsForApi : undefined,
        search: searchForApi || undefined,
        limit: pageSize,
      });

      setNormalCache({
        videos: filterAllowedAndDeduplicate(items),
        nextCursor,
        cacheKey: normalCacheKey,
      });
    } finally {
      setLoading(false);
    }
  }, [
    tagsForApi,
    searchForApi,
    pageSize,
    filterAllowedAndDeduplicate,
    normalCacheKey,
  ]);

  const refreshSavedVideos = useCallback(() => {
    const savedVideos = readSavedVideos();
    const filteredSavedVideos = filterSavedVideosLocally(savedVideos);

    setSavedCache({
      videos: filteredSavedVideos,
      cacheKey: savedCacheKey,
    });
  }, [filterSavedVideosLocally, savedCacheKey]);

  const loadMore = useCallback(async () => {
    if (savedOnly) return;
    if (!normalCache.nextCursor || loading || loadingMore) return;
    if (normalCache.cacheKey !== normalCacheKey) return;

    setLoadingMore(true);

    try {
      const { items, nextCursor } = await getVideos({
        tags: tagsForApi.length ? tagsForApi : undefined,
        search: searchForApi || undefined,
        limit: pageSize,
        cursor: normalCache.nextCursor,
      });

      setNormalCache((prev) => ({
        videos: filterAllowedAndDeduplicate([...prev.videos, ...items]),
        nextCursor,
        cacheKey: normalCacheKey,
      }));
    } finally {
      setLoadingMore(false);
    }
  }, [
    savedOnly,
    normalCache.nextCursor,
    normalCache.cacheKey,
    normalCacheKey,
    loading,
    loadingMore,
    tagsForApi,
    searchForApi,
    pageSize,
    filterAllowedAndDeduplicate,
  ]);

  useEffect(() => {
    if (savedOnly) {
      refreshSavedVideos();
      return;
    }

    const hasMatchingNormalCache =
      normalCache.cacheKey === normalCacheKey && normalCache.videos.length > 0;

    if (!hasMatchingNormalCache) {
      fetchNormalFirstPage();
    }
  }, [
    savedOnly,
    refreshSavedVideos,
    fetchNormalFirstPage,
    normalCache.cacheKey,
    normalCache.videos.length,
    normalCacheKey,
  ]);

  useEffect(() => {
    const handleSavedVideosUpdated = () => {
      refreshSavedVideos();
    };

    window.addEventListener("saved-videos-updated", handleSavedVideosUpdated);

    return () => {
      window.removeEventListener(
        "saved-videos-updated",
        handleSavedVideosUpdated
      );
    };
  }, [refreshSavedVideos]);

  const videos = savedOnly ? savedCache.videos : normalCache.videos;
  const hasMore = savedOnly ? false : Boolean(normalCache.nextCursor);

  return {
    videos,
    loading,
    loadingMore: savedOnly ? false : loadingMore,
    hasMore,
    loadMore,
  };
};